import { usePageContext } from "lib/context/page-context";
import { Button, FormContainer } from "blocks/components";
import {
  BlockId,
  blockIsFocused,
  FormAutomationFocusStateData,
} from "blocks/types";
import {
  FormBlock as FormBlockType,
  NotificationAction,
  NavigateAction,
  OpenExternalUrlAction,
} from "../schema";
import Blocks from "components/Blocks";
import BlockWrapper from "blocks/components/BlockWrapper";
import { useRouter } from "next/router";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { FocusWrapper } from "blocks/components/FocusWrapper";
import { trpc } from "utils/trpc";
import { parseUrlParamValue } from "blocks/utilities/url-param-helpers";
import { useTheme } from "lib/theme";
import { useTrackingContext } from "observability/tracking";
import { useCurrentProject } from "lib/context/current-project-context";
import { useCurrentPages } from "lib/context/current-pages-context";
import { useContentDatasource } from "lib/hooks/useContentDatasource";
import { parseHandlebars } from "lib/utils/parseHandlebars";
import { isAllowedDynamicDefaultValueForField } from "../../Field/utils";
import { navigateToPage } from "../lib/actions/navigation";
import { showNotification } from "../lib/actions/notification";
import { openExternalUrl } from "../lib/actions/openExternalUrl";
import { getFormDataAndQueryParams } from "../lib/getFormDataAndQueryParams";

type FormData = Record<string, any>;

const AUTOMATION_PANELS: FormAutomationFocusStateData["panel"][] = [
  "automation",
  "automation-new",
  "automation-edit-zap",
  "automation-edit-blockAction",
];

export function FormBlock(props: { block: FormBlockType; blockId: BlockId }) {
  const { config } = props.block;
  const theme = useTheme();
  const { data: datasource } = useContentDatasource();
  const { emitConsumerInteractionEvent } = useTrackingContext();

  const { pageId, projectId, isEditing, isThumbnail, isEmbedded, focusState } =
    usePageContext();

  const actionIsFocused: boolean =
    blockIsFocused(focusState) &&
    focusState.blockId === props.blockId &&
    AUTOMATION_PANELS.some((panel) => panel === focusState.data?.panel);

  const project = useCurrentProject();
  const pages = useCurrentPages();

  const utils = trpc.useContext();
  const createFormSubmission = trpc.formSubmissions.create.useMutation({
    onSuccess() {
      utils.tableFields.getChoices.invalidate();
    },
  });

  const router = useRouter();

  const defaultValues = props.block.children.reduce((prev, item) => {
    const { name, inputType } = item.config;
    let defaultValue: any;
    if (item.config.inputType === "date-picker" && item.config.defaultToNow) {
      defaultValue = new Date();
    } else if (
      item.config.defaultValue &&
      isAllowedDynamicDefaultValueForField(
        item.config.inputType,
        item.config.defaultValue.replace(/ /g, "")
      )
    ) {
      defaultValue = parseHandlebars(
        item.config.defaultValue.replace(/ /g, ""),
        datasource
      );
    } else {
      defaultValue = item.config.defaultValue;
    }
    const value = parseUrlParamValue(inputType, router.query[name]);
    return {
      ...prev,
      [name]: value ?? defaultValue,
    };
  }, {});

  const methods = useForm<FormData>({ mode: "onBlur", defaultValues });
  const { isSubmitting, isSubmitSuccessful } = methods.formState;

  const hasNavigationAction = config.triggers?.some(
    (action) => action.type === "navigate"
  );

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    const [formData, queryParamsData] = getFormDataAndQueryParams(
      props.block,
      data
    );

    const notificationAction = config.triggers?.find(
      (action): action is NotificationAction => action.type === "notification"
    );

    const navigateAction = config.triggers?.find(
      (action): action is NavigateAction => action.type === "navigate"
    );

    const externalUrlAction = config.triggers?.find(
      (action): action is OpenExternalUrlAction =>
        action.type === "openExternalUrl"
    );

    if (notificationAction) {
      showNotification(notificationAction);
    }

    if (config.id) {
      await createFormSubmission.mutateAsync({
        formId: config.id,
        values: formData,
      });
    }

    if (config.storageId && config.storageType === "tables") {
      utils.blockTable.queryRecords.invalidate();
      utils.blockKanban.queryRecords.invalidate();
      utils.blockKanban.get.invalidate();
    }

    emitConsumerInteractionEvent({
      interaction_goal: "submit form",
      event_action: "click",
      interfaces_project_id: projectId,
      interfaces_page_id: pageId,
      interfaces_component_type: "form",
      embedded_flag: isEmbedded,
    });

    if (navigateAction && !isEditing) {
      if (!project) throw new Error("Missing project");
      await navigateToPage(navigateAction, pages, queryParamsData, router);
    }

    if (externalUrlAction && !isEditing) {
      openExternalUrl(externalUrlAction, queryParamsData);
    }

    methods.reset();
  };

  return (
    <BlockWrapper
      blockId={props.blockId}
      block={props.block}
      isFocused={actionIsFocused ? false : undefined}
      maxWidth={
        config.style?.width
          ? theme.app.width[config.style?.width]
          : theme.app.width.narrow
      }
      alignment={config.style?.alignment}
    >
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FormContainer
          as="fieldset"
          disabled={
            isSubmitting ||
            (isSubmitSuccessful && hasNavigationAction) ||
            isThumbnail
          }
        >
          <FormProvider {...methods}>
            <Blocks
              blocks={props.block.children}
              bare
              parentId={props.blockId}
            />
          </FormProvider>

          <FocusWrapper isFocused={actionIsFocused} focusedBlockType="Button">
            <Button type="submit" isLoading={methods.formState.isSubmitting}>
              {config.submitText}
            </Button>
          </FocusWrapper>
        </FormContainer>
      </form>
    </BlockWrapper>
  );
}
